<template>
  <div v-if="!appIs.finishedIntialLoad">
    <slot name="loader">
      <div class="loading loading-large" />
    </slot>
  </div>

  <div v-else :class="{ 'container-padding': padding && compRequireActionCount, 'p-2': !padding }">
    <UtilMessage
      v-if="!compGuardRequires.auth && compRequireActionCount && showCompleteToContinueNotice"
      type="warning"
      class="mb-5 -mx-2"
      :class="{ '-mx-2': !padding }"
    >
      {{ $t('feedback.completeBeforeContinuing') }}
    </UtilMessage>

    <template v-if="compGuardRequires.auth">
      <UtilMessage type="warning" class="max-w-xl mb-4" :class="{ '-mx-2': !padding }">
        {{ loginToContinueMessage || $t('auth.loginFirst') }}
      </UtilMessage>

      <!-- Biometrics are triggered when form mounts, so check we aren't logged out if native app -->
      <FormAuthLogin
        v-if="!appIs.native || (appIs.native && !$store.state.auth.nativeLoggedOut)"
        @complete="complete()"
        :registerRedirect="false"
        class="max-w-xl"
        :class="{ '-m-2': !padding }"
      />
    </template>

    <!-- Parent is short on space, redirect user away to complete -->
    <template v-else-if="redirectToComplete && (compGuardRequires.user || compGuardRequires.company)">
      <UtilButton color="primary" to="/account/dashboard" :class="{ '-mx-2': !padding }">
        Complete your profile
      </UtilButton>
    </template>

    <template v-else-if="compGuardRequires.user">
      <h3>{{ $t('titles.aboutYou') }}</h3>

      <FormUser @complete="complete()" />
    </template>

    <template v-else-if="compGuardRequires.company">
      <h3>{{ $t('titles.aboutCompany') }}</h3>

      <FormCompany @complete="complete()" />
    </template>

    <!--<UtilSPVerification-->
    <!--v-else-if="compGuardRequires.farmPayVerification"-->
    <!--@complete="complete"-->
    <!--:showFarmpaySupportDetails="showFarmpaySupportDetails"-->
    <!--:showFraudWarning="false"-->
    <!--/>-->

    <!-- Offset the padding on the parent -->
    <div v-else :class="{ '-m-2': !padding, ...guardContainerClass }">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { filter, get } from 'lodash'
import { mapGetters } from 'vuex'
import UtilMessage from '@/components/utils/UtilMessage'
import UtilButton from '@/components/utils/UtilButton'

export default {
  name: 'UtilGuard',
  components: {
    UtilMessage,
    UtilButton,
    // Only lazy load a heavy component that isn't used everywhere
    FormAuthLogin: () => import('@/components/forms/FormAuthLogin'),
    FormUser: () => import('@/components/forms/FormUser'),
    FormCompany: () => import('@/components/forms/FormCompany')
    // UtilSPVerification: () => import('@/components/utils/payment/secure-payments/view-states/UtilSPVerification')
  },
  props: {
    showCompleteToContinueNotice: {
      type: Boolean,
      default: true
    },
    requireAuth: {
      type: Boolean,
      default: true
    },
    requireUser: {
      type: Boolean,
      default: true
    },
    requireCompany: {
      type: Boolean,
      default: true
    },
    requireFarmPayVerification: {
      type: Boolean,
      default: false
    },
    guardContainerClass: {
      type: Object,
      default: () => {
        return {}
      }
    },
    scrollOnComplete: {
      type: Boolean,
      default: true
    },
    redirectToComplete: {
      type: Boolean,
      default: false
    },
    padding: Boolean,
    showFarmpaySupportDetails: Boolean,
    loginToContinueMessage: String
  },
  watch: {
    // @complete is emitted from our login form but is often destroyed before it bubbles to parent
    'appIs.auth'(isAuth) {
      if (isAuth) {
        this.complete()
      }
    }
  },
  created() {
    if (this.requireFarmPayVerification) {
      this.$store.dispatch('securePayments/getProfile')
    }
  },
  computed: {
    ...mapGetters({
      isFarmPayVerified: 'securePayments/isVerified'
    }),

    compGuardRequires() {
      let guardRequires = {}

      guardRequires.auth = this.requireAuth && !this.appIs.auth
      guardRequires.user = this.requireUser && !this.compUserComplete
      guardRequires.company = this.requireCompany && !this.compCompanyComplete
      guardRequires.farmPayVerification = this.requireFarmPayVerification && !this.isFarmPayVerified

      return guardRequires
    },

    compRequireActionCount() {
      let requiredActions = filter(this.compGuardRequires, required => required)
      return requiredActions.length
    },

    // Checks to ensure user profile is complete
    compUserComplete() {
      if (this.app.user.firstName === '') {
        return false
      }

      // No numbers
      if (!get(this.app, 'user.mobiles[0].number') && !get(this.app, 'user.landlines[0].number')) {
        return false
      }

      return true
    },

    // Checks to ensure company profile is complete
    compCompanyComplete() {
      // TODO: Add all the checks here. It should be complete in order to catch any
      // legacy accounts that might be missing information
      if (!this.app.company.name) {
        return false
      }

      // Locations without postcodes
      if (!get(this.app, 'locations[0].code')) {
        return false
      }

      return true
    }
  },
  methods: {
    async complete() {
      if (this.scrollOnComplete) {
        this.$scroll.toTop()
      }

      if (this.compGuardRequires.farmPayVerification) {
        await this.$store.dispatch('securePayments/getProfile')
      }

      if (this.compRequireActionCount === 0) {
        this.$emit('complete')
      }
    }
  }
}
</script>
